"use client";

import { bisector } from "d3-array";
import { axisBottom, axisLeft } from "d3-axis";
import { rgb } from "d3-color";
import { scaleBand, scaleLinear, scaleLog } from "d3-scale";
import { interpolateYlGn } from "d3-scale-chromatic";
import { pointer, select } from "d3-selection";
import { curveCardinal, line } from "d3-shape";
import { annotation, annotationCalloutRect } from "d3-svg-annotation";
import { transition } from "d3-transition";
import React, { useEffect, useRef } from "react";

import ageStats from "./age-stats.json";
import ageAggStats from "./age_agg_stats.json";
import chartAnnotations from "./chart-annotations.js";
import indiaGridMap from "./indiaGridMap";
import num2string from "./num2string.js";

// let map = ({ india, states }, fn) => ({ india: fn('India', india), states: states.map(([name, dist]) => fn(name, dist)) });

const toFixed = (n) => (n && Number.isFinite(n) ? n.toFixed(2) : n);

export const gridMap = indiaGridMap.reduce((acc, state) => {
  acc[state.name] = state;
  return acc;
}, {});

if (typeof window != "undefined") window.ageStats = ageStats;

const defaultMargin = { top: 20, left: 60, right: 15, bottom: 55 };

// chartreuse
const genderRatioColor = rgb("#ff5722");
genderRatioColor.opacity = 0.9;
const colorG = scaleLog()
  .domain([0.8, 0.94])
  .range([genderRatioColor, genderRatioColor.brighter(2.5)])
  .clamp(true);

export const genderRatioLimit = 0.93;
export const diffColor = colorG(genderRatioLimit).toString();
export const adultLineColor = "burlywood";
export const popBarColor = "#78bb90";
export const areaBarColor = "#b89487";
export const gapBG = "lightgray";
export { ageStats, ageAggStats };

// [d.age,
// d.total, d.totalPerc,
// d.male, d.excessMale, d.malePerc, d.excessMalePerc,
// d.female, d.excessFemale, d.femalePerc, d.excessFemalePerc]

const [
  ageIdx,
  totalIdx,
  totalPercIdx,
  maleIdx,
  excessMaleIdx,
  malePercIdx,
  excessMalePercIdx,
  femaleIdx,
  excessFemaleIdx,
  femalePercIdx,
  excessFemalePercIdx,
] = [...Array(11).keys()];

const renderBars = (
  svg,
  distribution,
  name,
  i,
  width,
  height,
  totalPopulation,
  props,
  margin = defaultMargin,
) => {
  let isInverted = props.isInverted;
  const showRatioGrades = isInverted ? props.showRatioGrades : false;
  let chartHeight = (isInverted ? 2 : 1) * height;
  const stateData = props.state;
  const total = stateData.total;
  const showVerticalTicks = !props.hideGrid;
  const area = props.meta?.area;
  const maxArea = props.maxArea;

  const maxPercentage = 0.019;

  // - Ignore nostated.
  // - Convert to array
  // - Total (male + female) perc
  // - Trend line pre-calculation
  // - Math.max(y(mPoints[i]) - y(d.malePerc), 0)
  // - d.female / d.male > genderRatioLimit ?
  // - state totals
  const data = stateData.dist;

  const domain = data.map((d) => d[ageIdx]);
  const x = scaleBand().domain(domain).rangeRound([0, width]);
  let y = scaleLinear().domain([0, maxPercentage]).range([height, 0]).nice();

  const chart = svg
    .append("g")
    .attr("class", "chart")
    .style("opacity", props.isLegend ? 0.5 : 1)
    .attr("transform", `translate(${margin.left}, ${margin.top})`);

  const maleY = (d) => (isInverted ? 0 : y(d[malePercIdx]));
  const maleH = (d) => height - y(d[malePercIdx]);

  const femaleY = (d) => y(d[femalePercIdx]);
  const femaleH = (d) => height - y(d[femalePercIdx]);

  const c = "#616161";

  const mColor = rgb(props.maleColor || c);
  const mbColor = rgb(mColor).darker();
  mColor.opacity = 1;
  const mlColor = mColor.darker(0.5);
  mlColor.opacity = 0.8;

  const fColor = rgb(props.femaleColor || c);
  const fbColor = rgb(fColor).darker();
  fColor.opacity = 1;
  const flColor = fColor.darker(0.5);
  flColor.opacity = 0.8;

  const back = chart
    .selectAll(".back")
    .data([1])
    .enter()
    .append("g")
    .attr("class", "back");

  const showAdultAge = (show) => {
    const data = show ? [1] : [];
    const ageLinePadding = 30;
    const t = transition().duration(750);

    const adultLine = chart
      .select(".back")
      .selectAll(".adult-age-line")
      .data(data);

    adultLine
      .enter()
      .append("rect")
      .attr("class", "adult-age-line")
      .merge(adultLine)
      .attr("x", (d) => x(18) + x.bandwidth() / 4)
      .attr("y", (d) => ageLinePadding)
      .attr("width", x.bandwidth() / 2)
      .attr("height", chartHeight - 2 * ageLinePadding)
      .attr("fill", adultLineColor)
      .transition(t)
      .style("opacity", 1);

    adultLine.exit().remove();
  };

  let maleHalf = chart.selectAll(".male-half").data([1]);

  const bars = chart
    .selectAll("g.bars")
    .data([1])
    .enter()
    .append("g")
    .attr("class", "bars");

  const renderMaleBars = (state, isInverted) => {
    let maleBars = bars.selectAll(".male-bars").data(data);
    let femaleBars = bars.selectAll(".female-bars").data(data);

    const tb = transition().duration(1000);

    const mExtra = bars.selectAll(".male-extra-bars").data([]);
    const fExtra = bars.selectAll(".female-extra-bars").data([]);
    if (state != "flipped-split-same") {
      mExtra.exit().remove();
      fExtra.exit().remove();
    }

    const maxDomain = state.startsWith("tnc") ? 0.029 : 0.02;

    y = y.domain([0, maxDomain]).rangeRound([height, 0]);

    const genderSymbolData = state.startsWith("tnc") ? [] : [null];

    const fSymb = chart.selectAll(".f-symbol").data(genderSymbolData);
    const mSymb = chart.selectAll(".m-symbol").data(genderSymbolData);

    fSymb
      .enter()
      .append("text")
      .text("♀")
      .attr("class", "f-symbol gender-symbol")
      .attr("x", x(92))
      .attr("y", 200);
    fSymb.exit().remove();
    mSymb
      .enter()
      .append("text")
      .text("♂")
      .attr("class", "m-symbol gender-symbol")
      .attr("x", x(92))
      .attr("y", 320);
    mSymb.exit().remove();

    if (state.startsWith("tnc")) {
    }

    maleBars = maleBars
      .enter()
      .append("rect")
      .attr("class", "male-bars")
      .merge(maleBars);

    femaleBars = femaleBars
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "female-bars")
      .merge(femaleBars);

    switch (state) {
      case "tnc": // total-no-color
        femaleBars
          .style("fill", "lightgray")
          .style("stroke", "lightgray")
          .attr("x", (d, i) => x(d[ageIdx]))
          .attr("width", x.bandwidth())
          .style("stroke-width", 0)
          .transition(tb)
          .attr("y", (d) => y(d[femalePercIdx]))
          .style("stroke-dasharray", "none")
          .attr("height", (d) => y(0) - y(d[femalePercIdx]));

        maleBars
          .attr("x", (d) => x(d[ageIdx]))
          .attr("width", x.bandwidth())
          .style("fill", "lightgray")
          .style("stroke", "lightgray")
          .style("stroke-width", 0)
          .transition(tb)
          .style("stroke-dasharray", "none")
          .attr("y", (d) => y(d[totalPercIdx]))
          .attr("height", (d) => y(0) - y(d[totalPercIdx]));

        return;
      case "tnc5":
        femaleBars
          .attr("x", (d, i) => x(d[ageIdx]))
          .attr("width", x.bandwidth())
          .style("stroke", (_d, i) =>
            i % 5 ? "lightgray" : rgb("lightgray").darker(),
          )
          .style("stroke-width", 0)
          .style("stroke-dasharray", "none")
          .transition(tb)
          .style("fill", (_d, i) =>
            i % 5 ? "lightgray" : rgb("lightgray").darker(),
          )
          .attr("y", (d) => y(d[femalePercIdx]))
          .attr("height", (d) => y(0) - y(d[femalePercIdx]));

        maleBars
          .attr("x", (d) => x(d[ageIdx]))
          .attr("width", x.bandwidth())
          .style("stroke", (_d, i) =>
            i % 5 ? "lightgray" : rgb("lightgray").darker(),
          )
          .style("stroke-width", 0)
          .style("stroke-dasharray", "none")
          .transition(tb)
          .style("fill", (_d, i) =>
            i % 5 ? "lightgray" : rgb("lightgray").darker(),
          )
          .attr("y", (d) => y(d[totalPercIdx]))
          .attr("height", (d) => y(0) - y(d[totalPercIdx]));

        return;
      case "tnc-split": // total-no-color
        maleBars
          .attr("x", (d) => x(d[ageIdx]))
          .attr("width", x.bandwidth())
          .style("stroke", mbColor)
          .style("stroke-width", 1)
          .style("stroke-dasharray", (d) => {
            const h = y(0) - y(d[malePercIdx]);
            const w = x.bandwidth();
            return [w, h].join(",");
          })
          .attr("y", (d) => y(d[totalPercIdx]))
          .attr("height", (d) => y(0) - y(d[malePercIdx]))
          .transition(tb)
          .style("fill", (d, i) => (i % 5 ? mColor : mlColor));

        femaleBars
          .attr("x", (d, i) => x(d[ageIdx]))
          .attr("width", x.bandwidth())
          .style("stroke", fbColor)
          .style("stroke-width", 1)
          .style("stroke-dasharray", (d) => {
            const h = y(0) - y(d[femalePercIdx]);
            const w = x.bandwidth();
            return [w, h].join(",");
          })
          .attr("y", (d) => y(d[femalePercIdx]))
          .attr("height", (d) => y(0) - y(d[femalePercIdx]))
          .transition(tb)
          .style("fill", (d, i) => (i % 5 ? fColor : flColor));
        return;
      case "flipped-split":
        maleBars
          .style("fill", (d, i) => (i % 5 ? mColor : mlColor))
          .attr("x", (d) => x(d[ageIdx]))
          .transition(tb)
          .attr("y", (d) => (isInverted ? height : 0) + maleY(d))
          .attr("width", x.bandwidth())
          .attr("height", maleH)
          .attr("stroke", mbColor)
          .style("stroke-width", 1)
          .style("stroke-dasharray", (d) => {
            const h = maleH(d);
            const w = x.bandwidth();
            return [w, h].join(",");
          });

        femaleBars
          .style("fill", (d, i) => (i % 5 ? fColor : flColor))
          .attr("x", (d, i) => x(d[ageIdx]))
          .attr("width", x.bandwidth())
          .transition(tb)
          .style("stroke", fbColor)
          .style("stroke-width", 1)
          .style("stroke-dasharray", (d) => {
            const h = femaleH(d);
            const w = x.bandwidth();
            return [w, h].join(",");
          })
          .attr("y", femaleY)
          .attr("height", femaleH);
        return;
      case "flipped-split-same":
        const mc = scaleLinear()
          .domain([0, maxPercentage])
          .interpolate(() => interpolateYlGn)
          .clamp(true);

        const mPoints = stateData.maleLine;
        const fPoints = stateData.femaleLine;
        const color5 = "mediumseagreen";

        maleBars
          .attr("x", (d) => x(d[ageIdx]))
          .attr("y", (d) => (isInverted ? height : 0) + maleY(d))
          .attr("width", x.bandwidth())
          .attr("height", maleH)
          .attr("stroke", mbColor)
          .style("stroke-width", 1)
          .style("stroke-dasharray", (d, i) => {
            const h = maleH(d);
            const w = x.bandwidth();
            return [w, h].join(",");
          })
          .transition(tb)
          .style("fill", (d, i) => mc(mPoints[i]));

        const mExtra = bars.selectAll(".male-extra-bars").data(data);
        const fExtra = bars.selectAll(".female-extra-bars").data(data);

        mExtra
          .enter()
          .append("rect")
          .attr("class", "male-extra-bars")
          .merge(mExtra)
          .attr("x", (d) => x(d[ageIdx]))
          .attr("y", (d, i) => height + height - y(mPoints[i]))
          .attr("width", x.bandwidth())
          .transition(tb)
          .attr("height", (d, i) =>
            Math.max(y(mPoints[i]) - y(d[malePercIdx]), 0),
          )
          .style("fill", (d, i) => rgb(mc(mPoints[i])).darker(1));

        femaleBars
          .attr("x", (d, i) => x(d[ageIdx]))
          .attr("width", x.bandwidth())
          .style("stroke", fbColor)
          .style("stroke-width", 1)
          .style("stroke-dasharray", (d) => {
            const h = femaleH(d);
            const w = x.bandwidth();
            return [w, h].join(",");
          })
          .attr("y", femaleY)
          .attr("height", femaleH)
          .transition(tb)
          .style("fill", (d, i) => mc(fPoints[i]));

        fExtra
          .enter()
          .append("rect")
          .attr("class", "female-extra-bars")
          .merge(fExtra)
          .attr("x", (d) => x(d[ageIdx]))
          .attr("y", (d) => y(d[femalePercIdx]))
          .attr("width", x.bandwidth())
          .transition(tb)
          .attr("height", (d, i) =>
            Math.max(y(fPoints[i]) - y(d[femalePercIdx]), 0),
          )
          .style("fill", (d, i) => rgb(mc(fPoints[i])).darker(1));

        return;
    }
  };

  const flipMale = (barState) => {
    isInverted =
      barState == "flipped-split" || barState == "flipped-split-same";
    chartHeight = (isInverted ? 2 : 1) * height;

    const tb = transition().duration(1000);

    renderMaleBars(barState, isInverted);
  };

  const showXAxis = (show) => {
    const tb = transition().duration(800);

    const xAxis = axisBottom()
      .scale(x)
      .tickFormat((i) => (i % 10 ? "" : i))
      .tickSize(showVerticalTicks ? -2 * height : 0)
      .tickValues(x.domain().filter((d) => !(d % 5)));

    const axis = back.selectAll(".x.axis").data(show ? [null] : []);

    axis
      .enter()
      .append("g")
      .attr("class", "x axis")
      .merge(axis)
      .transition(tb)
      .attr("transform", () => `translate(0, ${chartHeight})`)
      .transition(transition().duration(1000))
      .call(xAxis);

    const axisLabel = chart.selectAll(".x.axis-label").data(show ? [null] : []);

    axisLabel
      .enter()
      .append("text")
      .attr("class", "x axis-label")
      .merge(axisLabel)
      .transition(tb)
      .attr("transform", () => `translate(0, ${chartHeight + 40})`)
      .attr("x", x(47))
      .text("Age");
  };

  const ratioGaps = (show) => {
    const mgaps = chart.selectAll(".extra-pop-male").data(show ? [1] : []);
    const td = transition().duration(750);

    mgaps
      .enter()
      .append("g")
      .attr("class", "extra-pop extra-pop-male")
      .attr("transform", () => `translate(0, ${isInverted ? height : 0})`)
      .selectAll(".bars")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "extra-pop-rect")
      .style("fill", rgb(gapBG))
      .style("opacity", 1)
      .attr("x", (d) => x(d[ageIdx]))
      .attr("y", (d) => 0)
      .attr("width", x.bandwidth())
      .transition(td)
      .style("stroke", "lightgray")
      .style("stroke-width", 1)
      .style("stroke-dasharray", (d) => {
        const h = height - y(d[excessMalePercIdx]);
        const w = x.bandwidth();
        return [w, h].join(",");
      })
      .attr("height", (d) => height - y(d[excessMalePercIdx]));

    mgaps.exit().remove();

    const fgaps = chart.selectAll(".extra-pop-female").data(show ? [1] : 0);
    fgaps
      .enter()
      .append("g")
      .attr("class", "extra-pop extra-pop-female")
      .selectAll(".bars")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "extra-pop-rect")
      .style("fill", rgb(gapBG))
      .style("opacity", 1)
      .attr("x", (d) => x(d[ageIdx]))
      .attr("y", (d) => y(d[excessFemalePercIdx]))
      .attr("width", x.bandwidth())
      .transition(td)
      .style("stroke", "lightgray")
      .style("stroke-width", 1)
      .style("stroke-dasharray", (d) => {
        const h = height - y(d[excessFemalePercIdx]);
        const w = x.bandwidth();
        return [w, h].join(",");
      })
      .attr("height", (d) => height - y(d[excessFemalePercIdx]));
    fgaps.exit().remove();
  };

  const ratioGapColor = (show) => {
    const gaps = chart.selectAll(".extra-gender-ratio").data(show ? [1] : []);
    const td = transition().duration(750);

    gaps
      .enter()
      .append("g")
      .attr("class", "extra-gender-ratio")
      .attr("transform", () => `translate(0, ${isInverted ? height : 0})`)
      .selectAll(".bars")
      .data(data.filter((d) => d[ageIdx] <= 15))
      .enter()
      .append("rect")
      .style("fill", (d, i) =>
        d[femaleIdx] / d[maleIdx] > genderRatioLimit
          ? rgb(gapBG)
          : colorG(d[femaleIdx] / d[maleIdx]),
      )
      .style("opacity", 1)
      .attr("x", (d) => x(d[ageIdx]))
      .attr("y", (d) => 0)
      .attr("width", x.bandwidth())
      .attr("stroke", (d) =>
        d[femaleIdx] / d[maleIdx] > genderRatioLimit
          ? "lightgray"
          : rgb("#db8400e6"),
      )
      .transition(td)
      .style("stroke-width", 1)
      .style("stroke-dasharray", (d) => {
        const h = height - y(d[excessMalePercIdx]);
        const w = x.bandwidth();
        return [w, h, w, h].join(",");
      })
      .attr("height", (d) => height - y(d[excessMalePercIdx]));

    gaps.exit().remove();
  };

  const trendLines = (show) => {
    // const fPoints = blur(data.map((d) => d.femalePerc)).map((d, i) => [i, d]);
    const fPoints = stateData.femaleLine.map((d, i) => [i, d]);
    const fCurve = chart.selectAll(".female-trend").data(show ? [null] : []);

    fCurve
      .enter()
      .append("path")
      .attr("class", "trend-line female-trend")
      .join(".female-trend")
      .datum(fPoints)
      .attr(
        "d",
        line()
          .curve(curveCardinal)
          .x((d) => x(d[0]))
          .y((d) => y(d[1])),
      );
    fCurve.exit().remove();

    // const mPoints = blur(data.map((d) => d.male / total)).map((d, i) => [i, d]);
    const mPoints = stateData.maleLine.map((d, i) => [i, d]);
    const mCurve = chart
      .selectAll(".male-trend")
      .data(show && isInverted ? [null] : []);

    mCurve
      .enter()
      .append("path")
      .attr("class", "trend-line male-trend")
      .join(".male-trend")
      .attr("transform", () => `translate(0, ${isInverted ? height : 0})`)
      .datum(mPoints)
      .attr(
        "d",
        line()
          .curve(curveCardinal)
          .x((d) => x(d[0]))
          .y((d) => height - y(d[1])),
      );
    mCurve.exit().remove();
  };

  const tickValues = [0.0, 0.01];
  const formatY = (x) =>
    [0, 0.01, 0.02, 0.03].includes(x) ? x * 100 + "%" : "";

  let smLabels = chart.selectAll(".small-multiple-labels").data([null]);
  smLabels = smLabels
    .enter()
    .append("g")
    .attr("class", "small-multiple-labels")
    .merge(smLabels);

  const showSMLabels = (code) => {
    const data = code ? [null] : [];

    let shortcode = smLabels.selectAll(".state-shortcode").data(data);

    shortcode
      .enter()
      .append("text")
      .attr("class", "state-shortcode")
      .attr("x", x(100))
      .attr("y", 110)
      .text(`${code}`);
    shortcode.exit().remove();

    const lineHeight = 15;

    const totalOffset = 25;
    const areaOffset = 0;

    let bg = smLabels.selectAll(".total-pop-bg").data(data);

    bg.enter()
      .append("rect")
      .attr("class", "total-pop-bg")
      .attr("x", x(0))
      .attr("y", totalOffset)
      .attr("width", 101 * x.bandwidth())
      .attr("height", lineHeight);

    bg.exit().remove();

    let populationProportion = smLabels.selectAll(".total-pop").data(data);

    populationProportion
      .enter()
      .append("rect")
      .attr("class", "total-pop")
      .attr("fill", popBarColor)
      .attr("x", x(0))
      .attr("y", totalOffset)
      .attr("width", (101 * x.bandwidth() * total) / props.maxStatePop)
      .attr("height", lineHeight);
    populationProportion.exit().remove();

    let marker10th = smLabels
      .selectAll(".total-markers")
      .data(code ? [1, 2, 3, 4, 5, 6, 7, 8, 9] : []);

    marker10th
      .enter()
      .append("rect")
      .attr("class", "total-markers")
      .attr("x", (d) => x(10 * d))
      .attr("y", totalOffset)
      .attr("width", 10)
      .attr("height", lineHeight);

    marker10th.exit().remove();

    if (!area) console.log("no area: ", name);
    if (area) {
      let areaBG = smLabels.selectAll(".state-area-bg").data(data);

      areaBG
        .enter()
        .append("rect")
        .attr("class", "state-area-bg")
        .merge(areaBG)
        .attr("x", 0)
        .attr("y", areaOffset)
        .attr("width", width)
        .attr("height", lineHeight);

      areaBG.exit().remove();

      let areaRect = smLabels.selectAll(".state-area").data(data);

      let areaY = scaleLinear().domain([0, maxArea]).range([0, width]);

      areaRect
        .enter()
        .append("rect")
        .attr("class", "state-area")
        .merge(areaRect)
        .attr("fill", areaBarColor)
        .attr("x", 0)
        .attr("y", areaOffset)
        .attr("width", areaY(area))
        .attr("height", lineHeight);

      areaRect.exit().remove();

      let area10th = smLabels
        .selectAll(".area-markers")
        .data(code ? [1, 2, 3, 4, 5, 6, 7, 8, 9] : []);

      area10th
        .enter()
        .append("rect")
        .attr("class", "area-markers")
        .attr("x", (d) => x(10 * d))
        .attr("y", areaOffset)
        .attr("width", 10)
        .attr("height", lineHeight);

      area10th.exit().remove();
    }
  };

  const showYAxis = (show, flipped) => {
    const yAxis = axisLeft()
      .scale(y)
      .tickFormat(formatY)
      .tickValues([0, 0.01, 0.02, 0.03])
      .tickSize(-width);

    const yTicks = back.selectAll(".axis-y").data(show ? [1] : []);

    yTicks
      .enter()
      .append("g")
      .attr("class", "axis axis-y")
      .merge(yTicks)
      .call(yAxis);
    yTicks.exit().remove().call(yAxis);

    const y1Axis = axisLeft()
      .scale(y.copy().range([0, height]))
      .tickFormat(formatY)
      .tickValues([0.01, 0.02, 0.03])
      .tickSize(-width);

    const y1Ticks = back
      .selectAll(".axis-y1")
      .data(show && flipped ? [null] : []);

    y1Ticks
      .enter()
      .append("g")
      .attr("class", "axis axis-y1")
      .merge(y1Ticks)
      .attr("transform", `translate(0, ${height})`)
      .call(y1Axis);
    y1Ticks.exit().remove().call(y1Axis);

    const yAxisLabel = back.selectAll(".y.axis-label").data(show ? [null] : []);

    yAxisLabel
      .enter()
      .append("text")
      .attr("class", "y axis-label")
      .attr(
        "transform",
        `translate(${-40}, ${flipped ? height : height / 2}) rotate(-90)`,
      )
      .merge(yAxisLabel)
      .transition(transition().duration(800))
      .attr(
        "transform",
        `translate(${-40}, ${flipped ? height : height / 2}) rotate(-90)`,
      )
      .style("text-anchor", "middle")
      .text("Population percentage");

    yAxisLabel.exit().remove();
  };

  const showGenderLabel = (show) => {
    const leftMargin = 50;
    const toPerc = (n) => `${toFixed((100 * n) / total)}%`;
    const genderLabels = back
      .selectAll(".gender-labels")
      .data(show ? [null] : []);

    genderLabels.join(
      (enter) => {
        enter
          .append("text")
          .attr("class", "split-label")
          .attr("x", x(100))
          .attr("y", y(0) - 65)
          .attr("text-anchor", "end")
          .text("Female");

        enter
          .append("text")
          .attr("class", "split-label")
          .attr("x", x(100))
          .attr("y", y(0) + 65)
          .attr("text-anchor", "end")
          .text("Male");
      },
      (update) => update,
      (exit) => exit.remove(),
    );
  };

  if (props.prod) {
    chart
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("height", chartHeight)
      .attr("width", width - margin.left)
      .attr("fill", "none")
      .style("pointer-events", "all");
  }

  if (props.annotations) {
    const annotations = props.annotations.map((annName) => {
      let point = null;
      let pointX = null;
      let pointY = null;
      let h = null;

      if (annName == "legend-adult-line") {
        point = data[18];
        pointX = x(point[ageIdx]) - 5;
        pointY = 80;
      } else if (annName == "legend-trend-line") {
        point = data[47];
        pointX = x(47) + x.bandwidth() / 2;
        pointY = 310;
      } else if (annName == "legend-shortcode") {
        pointX = x(100) - 40;
        pointY = 50;
      } else if (annName == "legend-up-comparison") {
        pointX = x(50);
        pointY = 25;
      } else if (annName == "legend-rj-comparison") {
        pointX = x(10);
        pointY = 0;
      } else if (annName == "legend-diff" || annName == "diff-chart") {
        point = data[2];
        h = height - y(point[excessMalePercIdx]);
      } else {
        point = data[10];
        h = height - y(point[excessMalePercIdx]);
      }

      return chartAnnotations[annName]({
        width: x.bandwidth(),
        height: point && h,
        data: point,
        x: pointX,
        y: pointY,
      });
    });

    const makeAnnotations = annotation()
      // also can set and override in the note.padding property
      // of the annotation object
      .notePadding(15)
      .type(annotationCalloutRect)
      // accessors & accessorsInverse not needed
      // if using x, y in annotations JSON
      .accessors({
        x: (d) => x(d[ageIdx]),
        y: (d) => (isInverted ? height : 0),
      })
      .accessorsInverse({
        age: (d) => data[x.invert(d.x)],
        female: (d) => y.invert(d.y),
      })
      .annotations(annotations);

    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .attr("class", "annotation-group")
      .call(makeAnnotations);
  }

  if (typeof window != "undefined") {
    window.flipMale = flipMale;
    window.showAdultAge = showAdultAge;
    window.ratioGaps = ratioGaps;
    window.ratioGapColor = ratioGapColor;
    window.trendLines = trendLines;
    window.showSMLabels = showSMLabels;
    window.showYAxis = showYAxis;
    window.showXAxis = showXAxis;
    window.showLabels = () => {
      showYAxis(true, true);
      showXAxis(true);
      showSMLabels(props.shortCode);
      showGenderLabel(true);
    };
  }

  svg
    .on("mousemove", moved)
    .on("mouseout", () => select("#tooltip").style("opacity", 0));
  /* .on("mouseenter touchstart", entered)
   * .on("mouseout touchend", left); */

  if (!props.disableClick) {
    svg.on("click", function (event) {
      const svg = select(this);
      event.preventDefault();
      svg.classed("state-highlight", !svg.classed("state-highlight"));
    });
  }

  function moved(event) {
    const bisect = bisector((d) => margin.left + x(d)).left;

    const m = pointer(event);
    const age = bisect(domain, m[0]) - 1;

    if (!data[age]) return;
    const d = data[age];
    const diff = d[maleIdx] - d[femaleIdx];

    let diffStr =
      diff < 0
        ? `About ${num2string(
            -diff,
            true,
          )} more female's. </br>i.e birth ratio of <b>${toFixed(
            d[femaleIdx] / d[maleIdx],
          )}</b>.`
        : `About ${num2string(
            diff,
            true,
          )} more male's. </br>i.e birth ratio of <b>${toFixed(
            d[femaleIdx] / d[maleIdx],
          )}</b>.`;

    const fperc = toFixed(100 * d[femalePercIdx]);
    const mperc = toFixed(100 * d[malePercIdx]);

    const tip = select("#tooltip");
    tip
      .style("opacity", 1)
      .style("left", event.clientX + 10 + "px")
      .style("top", event.clientY + 10 + "px");
    tip.html(`
    <h2 class="name">${name}</h2>

    <div> Population: <b>${num2string(total, true)} </b></div>
    <div> Area: <b>${num2string(area)} Km²</b></div>

    <div>
    Population at age ${age}: <b>${num2string(d[totalIdx], true)} </b>
    </div>

    <div class="tooltip-gender female">
      <b>Female:</b> ${num2string(d[femaleIdx], true)} (${fperc}% of total).
    </div>

    <div class="tooltip-gender male">
    <b>Male:</b> ${num2string(d[maleIdx], true)} (${mperc}% of total).
    </div>

<div>${diffStr} </div>
`);
  }

  if (true) {
    const setupScroll = () => {
      const scrollama = require("scrollama");
      // setup the instance, pass callback functions
      const scroller = scrollama();

      const steps = [
        () => {
          flipMale("tnc");
          ratioGaps(false);
          ratioGapColor(false);
          trendLines(false);
          showSMLabels(null);
          showAdultAge(false);
          showYAxis(true, false);
          showXAxis(true);
        },
        () => {
          flipMale("tnc5");
          ratioGaps(false);
          ratioGapColor(false);
          trendLines(false);
          showSMLabels(null);
          showAdultAge(false);
          showYAxis(true, false);
          showXAxis(true);
        },
        () => {
          flipMale("tnc-split");
          ratioGaps(false);
          ratioGapColor(false);
          trendLines(false);
          showSMLabels(null);
          showAdultAge(false);
          showYAxis(true, false);
          showXAxis(true);
        },
        () => {
          flipMale("flipped-split");
          ratioGaps(false);
          ratioGapColor(false);
          trendLines(false);
          showSMLabels(null);
          showAdultAge(false);
          showYAxis(true, true);
          showXAxis(true);
        },
        () => {
          flipMale("flipped-split");
          ratioGaps(true);
          ratioGapColor(false);
          trendLines(false);
          showSMLabels(null);
          showAdultAge(false);
          showYAxis(true, true);
          showXAxis(true);
        },
        () => {
          flipMale("flipped-split");
          ratioGaps(true);
          ratioGapColor(true);
          trendLines(false);
          showSMLabels(null);
          showAdultAge(false);
          showYAxis(true, true);
          showXAxis(true);
        },
        () => {
          flipMale("flipped-split");
          ratioGaps(true);
          ratioGapColor(true);
          trendLines(true);
          showSMLabels(null);
          showAdultAge(false);
          showYAxis(true, true);
          showXAxis(true);
        },
        () => {
          flipMale("flipped-split-same");
          ratioGaps(true);
          ratioGapColor(true);
          trendLines(true);
          showSMLabels(null);
          showAdultAge(false);
          showYAxis(true, true);
          showXAxis(true);
        },
        () => {
          flipMale("flipped-split-same");
          ratioGaps(true);
          ratioGapColor(true);
          trendLines(true);
          showSMLabels(null);
          showAdultAge(true);
          showYAxis(true, true);
          showXAxis(true);
        },
        () => {
          flipMale("flipped-split-same");
          ratioGaps(true);
          ratioGapColor(true);
          trendLines(true);
          showSMLabels(props.shortCode);
          showAdultAge(true);
          showYAxis(true, true);
          showXAxis(true);
        },
      ];

      const indiaSmIndex = parseInt(
        document.querySelector(".india-sm").dataset.stepIndex,
        10,
      );

      scroller
        .setup({ step: ".step", offset: 0.33 })
        .onStepEnter(({ element, index }) => {
          const isSM = element.parentElement.classList.contains("india-sm");
          for (let el of document.querySelectorAll(".india-sm .step")) {
            el.classList.remove("current-step");
          }

          if (isSM) {
            for (let el of document.querySelectorAll(`.india-sm svg`)) {
              el.classList.remove("highlight", "state-highlight");
            }
            if (index >= indiaSmIndex)
              document
                .querySelector(".india-sm")
                .classList.add("sm-step-opacity");

            if (element.dataset.states) {
              element.dataset.states.split(",").forEach((id) => {
                const highlight = !!element.dataset.highlight;
                for (let el of document.querySelectorAll(`.state-${id}`)) {
                  el.classList.add("highlight");
                  if (highlight) el.classList.add("state-highlight");
                }
              });
            }
          }

          element.classList.add("current-step");
          if (!isSM && steps[index]) {
            steps[index]();
          }
        })
        .onStepExit(({ element, index, direction }) => {
          const isSM = element.parentElement.classList.contains("india-sm");
          if (isSM && index <= indiaSmIndex && direction == "up") {
            document
              .querySelector(".india-sm")
              .classList.remove("sm-step-opacity");
          }
          element.classList.remove("current-step");
        });
    };

    if (props.scrollSteps) {
      if (document.readyState == "complete") {
        setupScroll();
      } else {
        window.addEventListener("load", setupScroll);
      }
      flipMale("tnc");
      ratioGaps(false);
      ratioGapColor(false);
      trendLines(false);
      showAdultAge(false);
      showYAxis(true, false);
      showXAxis(true);
    } else {
      if (props.steps) {
        if (document.readyState == "complete") {
          setupScroll();
        } else {
          window.addEventListener("load", setupScroll);
        }
      }
      flipMale("flipped-split-same");
      ratioGaps(true);
      ratioGapColor(true);
      trendLines(true);
      showSMLabels(props.shortCode);
      showAdultAge(true);
      if (props.showAxis) {
        showYAxis(true, true);
        showXAxis(true);
      }
    }
  }
};

const IndiaSplit = (props) => {
  const svgRef = useRef(null);

  const width = 610;
  const height = 0.4 * width;
  const totalHeight = height * 2;

  const margin = props.margin || defaultMargin;

  useEffect(() => {
    if (!svgRef.current) return;

    const { title } = props;

    renderBars(
      select(svgRef.current),
      props.data,
      title,
      0,
      width,
      height,
      props.total,
      props,
      margin,
    );
  }, [svgRef, width, height]);

  const viewBoxW = width + margin.left + margin.right;
  const viewBoxH = totalHeight + margin.top + margin.bottom;
  // (props.isInverted ? totalHeight : height) + margin.top + margin.bottom;

  return (
    <svg
      style={props.style}
      className={`${props.className} state-distribution inline`}
      viewBox={`0 0 ${viewBoxW} ${viewBoxH}`}
      ref={svgRef}
    />
  );
};

const India = (props) => (
  <IndiaSplit
    data={ageAggStats.india.dist}
    meta={gridMap["India"]}
    state={ageAggStats.india}
    total={ageAggStats.india.total}
    maleColor={props.maleColor}
    femaleColor={props.femaleColor}
    {...props}
  />
);

export const maxStatePop = ageAggStats.states["Uttar Pradesh"].total;

export const maxArea = gridMap["Rajasthan"].area;

const States = (props) => {
  const {
    states,
    india: { dist, total },
  } = ageAggStats;

  return (
    <div
      style={{
        gridTemplateColumns: "repeat(16, 1fr)",
        gridTemplateRows: "repeat(16, 1fr)",
        alignItems: "center",
        margin: "0 auto",
      }}
      className="state-grid grid"
    >
      {Object.keys(states)
        .map((k) => {
          let { x, y, id } = gridMap[k];
          x = x + 1;
          return (
            <IndiaSplit
              {...props}
              style={{
                gridColumn: `${x * 2 - 1} / ${x * 2 + 1}`,
                width: "100%",
                gridRow: `${y * 2 - 1} / ${y * 2 + 1}`,
                transformOrigin: `${x > 5 ? "right" : "left"} ${
                  y > 5 ? "bottom" : "top"
                }`,
              }}
              className={`inline row-span-1 col-span-1 state-${id}`}
              isInverted
              key={k}
              data={states[k].dist}
              state={states[k]}
              total={total}
              title={k}
              meta={gridMap[k]}
              maxArea={maxArea}
              shortCode={id}
              showRatioGrades
              showTrendLines
              showLabel={false}
              maleColor={props.maleColor}
              femaleColor={props.femaleColor}
              maxStatePop={maxStatePop}
              margin={{ top: 5, bottom: 5, left: 20, right: 20 }}
            />
          );
        })
        .concat([
          <IndiaSplit
            key="legend"
            className={`${props.className || ""} sm-legend-state`}
            {...props}
            style={{
              gridColumn: `10 / 14`,
              gridRow: `11 / 15`,
              width: "100%",
              border: "1px solid grey",
              transformOrigin: "right bottom",
            }}
            isInverted
            title="Maharashtra"
            data={states["Maharashtra"].dist}
            state={states["Maharashtra"]}
            total={total}
            shortCode="MH"
            meta={gridMap["Maharashtra"]}
            maxArea={maxArea}
            showLabel
            showSplitLabel
            showRatioGrades
            showTrendLines
            annotations={[
              "legend-diff",
              "legend-adult-line",
              "legend-trend-line",
              "legend-shortcode",
              "legend-up-comparison",
              "legend-rj-comparison",
            ]}
            maleColor={props.maleColor}
            femaleColor={props.femaleColor}
            maxStatePop={maxStatePop}
            margin={{ top: 140, bottom: 70, left: 140, right: 40 }}
            isLegend
          />,
          <div
            key="max-states"
            className="text-xs hidden lg:block dark-gray self-start"
            style={{
              gridColumn: `11 / 18`,
              gridRow: `1 / 3`,
            }}
          >
            <span className="font-mono">
              <span className="text-zink-400">Max population:</span>{" "}
              <span className="font-bold text-zink-900">UP</span> (
              <span className="font-bold text-zink-900">200 million</span>){" "}
              <span>approx</span>
            </span>
            <br />
            <span className="font-mono">
              <span className="text-zink-400">Max area:</span>{" "}
              <span className="font-bold text-zink-900">RJ</span> (
              <span className="font-bold text-zink-900">350k Km²</span>){" "}
              <span>approx</span>
            </span>
          </div>,
        ])}
    </div>
  );
};

export const IndiaTotal = India;

export default States;
